import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import type { DocCategory, Document, ServiceEvent, VehicleDetail } from 'used-vehicle-contracts';
import { DocumentHttpError, replaceDocument, uploadDocument } from '../../../apis/documents-http';
import { orpc } from '../../../apis/orpc-query';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../../components/ui/DataTable';
import { Dialog } from '../../../components/ui/Dialog';
import { DocumentFrame } from '../../../components/ui/DocumentFrame';
import { DocumentImage } from '../../../components/ui/DocumentImage';
import { FileInput } from '../../../components/ui/FileInput';
import { formatBytes, formatDay, formatDayTime, formatKm, formatMoney } from '../../../lib/format';
import { serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { DocumentMetaSheet, type DocumentMetaSubmission } from '../../documents/DocumentMetaSheet';
import { acceptFor, REQUIRED_PHOTO_CATEGORIES } from '../../documents/documents-display';
import { HistoryEventSheet, type HistoryEventSubmission } from '../../history/HistoryEventSheet';

export interface VehicleDocumentsTabProps {
  vehicle: VehicleDetail;
  /** Called after every write so the dossier header's counts follow along. */
  onChanged?: () => void;
}

/** What a pending or failed upload looks like while it is happening. */
interface UploadState {
  /** The slot or action it belongs to, so two uploads never share one progress bar. */
  key: string;
  fileName: string;
  progress: number;
  error: string | null;
  /** Kept so "Retry" can send the very same file again without asking for it twice (PP AC17). */
  retry: (() => void) | null;
}

/**
 * The Documents tab of `/vehicles/:id` (plan §7).
 *
 * Four things on this screen are product decisions rather than layout:
 *
 *  - **Every image and PDF is fetched with the session's bearer token** and rendered from an object URL
 *    (plan §2.5). There is no URL anywhere on this page that would work if it were copied out.
 *  - **A sold vehicle still accepts documents.** Every other tab of a SOLD dossier is read-only
 *    (plan §5.6), and this one is not, because the invoice for work done last month arrives after the
 *    car has gone.
 *  - **Nothing here changes a price.** The note under the upload area says so in the person's own
 *    language: adding a report does not touch the inspection or the valuation (PP AC12), and a
 *    historical amount is evidence that never enters CTA costs (PP AC07).
 *  - **A failed upload is retryable in place.** The file is kept in the closure, so "Retry" re-sends it
 *    rather than reopening the picker (PP AC17).
 */
export const VehicleDocumentsTab = ({ vehicle, onChanged }: VehicleDocumentsTabProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const vehicleId = vehicle.id;

  const documentsQuery = useQuery(orpc.documents.list.queryOptions({ input: { vehicleId } }));
  const photoSetQuery = useQuery(orpc.documents.photoSet.queryOptions({ input: { vehicleId } }));
  const historyQuery = useQuery(orpc.history.list.queryOptions({ input: { vehicleId } }));
  const costsQuery = useQuery(orpc.costs.list.queryOptions({ input: { owner: { type: 'VEHICLE', id: vehicleId } } }));

  const updateMeta = useMutation(orpc.documents.updateMeta.mutationOptions());
  const addHistory = useMutation(orpc.history.add.mutationOptions());
  const updateHistory = useMutation(orpc.history.update.mutationOptions());
  const removeHistory = useMutation(orpc.history.remove.mutationOptions());

  const [upload, setUpload] = useState<UploadState | null>(null);
  const [preview, setPreview] = useState<Document | null>(null);
  const [metaTarget, setMetaTarget] = useState<Document | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [historyTarget, setHistoryTarget] = useState<ServiceEvent | null>(null);
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const documents = documentsQuery.data?.items ?? [];
  const costItems = costsQuery.data?.items ?? [];
  const historyEvents = historyQuery.data?.items ?? [];
  const present = new Set(photoSetQuery.data?.present ?? []);

  const byCategory = (category: DocCategory): Document | null => documents.find((row) => row.category === category) ?? null;

  const damagePhotos = documents.filter((row) => row.category === 'PHOTO_DAMAGE');
  const licence = byCategory('LICENSE');
  const reports = documents.filter((row) => row.category === 'EXTERNAL_REPORT' || row.category === 'INVOICE' || row.category === 'OTHER');

  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.documents.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.documents.photoSet.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.history.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.costs.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.vehicles.listActivity.key() }),
    ]);

    onChanged?.();
  }, [onChanged, queryClient]);

  /**
   * One upload, with its progress and its retry. The `run` closure captures the `File`, which is what
   * makes "Retry" send the same bytes rather than asking the person to find the file again.
   */
  const startUpload = useCallback(
    (key: string, file: File, send: (onProgress: (fraction: number) => void) => Promise<Document>) => {
      const run = (): void => {
        setUpload({ key, fileName: file.name, progress: 0, error: null, retry: null });

        void send((progress) => setUpload((current) => (current?.key === key ? { ...current, progress } : current)))
          .then(async () => {
            setUpload(null);
            await refresh();
          })
          .catch((caught: unknown) => {
            // The service names its refusals with a `reasonKey`. The ones the web has wording of its
            // own for are translated; anything else keeps the service's English sentence, which is
            // written for the person reading it (plan §11 fix 2).
            const message =
              caught instanceof DocumentHttpError
                ? caught.reasonKey === null
                  ? caught.message
                  : t(caught.reasonKey, { defaultValue: caught.message })
                : (serverMessage(caught) ?? t(submitErrorKey(caught)));

            setUpload({ key, fileName: file.name, progress: 1, error: message, retry: run });
          });
      };

      run();
    },
    [refresh, t],
  );

  const uploadInto = (category: DocCategory, file: File, title?: string) =>
    startUpload(`upload:${category}:${file.name}`, file, (onProgress) => uploadDocument(vehicleId, file, { category, title }, onProgress));

  const replaceWith = (document: Document, file: File) =>
    startUpload(`replace:${document.id}`, file, (onProgress) => replaceDocument(document.id, file, onProgress));

  const onMetaSubmit = async (submission: DocumentMetaSubmission): Promise<void> => {
    setMetaError(null);

    try {
      await updateMeta.mutateAsync(submission);
      await refresh();
      setMetaTarget(null);
    } catch (caught: unknown) {
      setMetaError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  const onHistorySubmit = async (submission: HistoryEventSubmission): Promise<void> => {
    setHistoryError(null);

    try {
      if (submission.id === null) {
        const { id: _id, ...rest } = submission;

        await addHistory.mutateAsync({ vehicleId, ...rest });
      } else {
        await updateHistory.mutateAsync({ ...submission, id: submission.id });
      }

      await refresh();
      setHistoryOpen(false);
      setHistoryTarget(null);
    } catch (caught: unknown) {
      setHistoryError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  const sizeHint = t('documents.limitsHint');
  const isUploading = upload !== null && upload.error === null;

  const reportColumns: DataTableColumns<Document> = [
    {
      id: 'title',
      accessorKey: 'title',
      header: t('documents.columnTitle'),
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{row.original.title}</p>
          <p className="truncate text-xs text-muted-foreground">{row.original.originalName}</p>
        </div>
      ),
    },
    {
      id: 'category',
      header: t('documents.columnCategory'),
      cell: ({ row }) => <Badge>{t(`enums.docCategory.${row.original.category}`)}</Badge>,
    },
    {
      id: 'documentDate',
      header: t('documents.columnDocumentDate'),
      cell: ({ row }) => <span className="text-sm">{formatDay(row.original.documentDate)}</span>,
    },
    {
      id: 'size',
      header: t('documents.columnSize'),
      cell: ({ row }) => (
        <span
          dir="ltr"
          className="tabular-nums text-sm"
        >
          {formatBytes(row.original.sizeBytes)}
        </span>
      ),
    },
    {
      id: 'uploaded',
      header: t('documents.columnUploaded'),
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate text-sm">{row.original.uploadedByName ?? t('common.notRecorded')}</p>
          <p className="truncate text-xs text-muted-foreground">{formatDayTime(row.original.createdAt)}</p>
        </div>
      ),
    },
    {
      id: 'costItem',
      header: t('documents.columnCostItem'),
      cell: ({ row }) => {
        const item = costItems.find((cost) => cost.id === row.original.costItemId);

        return <span className="text-sm text-muted-foreground">{item ? item.name : t('common.notRecorded')}</span>;
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPreview(row.original)}
          >
            {t('documents.view')}
          </Button>
          <FileInput
            id={`replace-${row.original.id}`}
            label={t('documents.replace')}
            size="sm"
            accept={acceptFor(row.original.category)}
            isBusy={isUploading && upload?.key === `replace:${row.original.id}`}
            onSelect={(file) => replaceWith(row.original, file)}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setMetaError(null);
              setMetaTarget(row.original);
            }}
          >
            {t('actions.edit')}
          </Button>
        </div>
      ),
    },
  ];

  const historyColumns: DataTableColumns<ServiceEvent> = [
    {
      id: 'eventDate',
      accessorKey: 'eventDate',
      header: t('history.columnDate'),
      cell: ({ row }) => <span className="text-sm">{formatDay(row.original.eventDate)}</span>,
    },
    {
      id: 'odometer',
      header: t('history.columnOdometer'),
      cell: ({ row }) => (
        <span
          dir="ltr"
          className="tabular-nums text-sm"
        >
          {row.original.odometerKm === null ? t('common.notRecorded') : formatKm(row.original.odometerKm)}
        </span>
      ),
    },
    {
      id: 'provider',
      header: t('history.columnProvider'),
      cell: ({ row }) => <span className="text-sm">{row.original.provider}</span>,
    },
    {
      id: 'category',
      header: t('history.columnCategory'),
      cell: ({ row }) => <Badge>{t(`enums.serviceCategory.${row.original.category}`)}</Badge>,
    },
    {
      id: 'description',
      header: t('history.columnDescription'),
      cell: ({ row }) => <p className="max-w-xs truncate text-sm">{row.original.description}</p>,
    },
    {
      id: 'amount',
      header: t('history.columnAmount'),
      cell: ({ row }) => (
        <div className="min-w-0">
          <span
            dir="ltr"
            className="tabular-nums text-sm"
          >
            {row.original.amount === null ? t('common.notRecorded') : formatMoney(row.original.amount, { exact: true })}
          </span>
          <p className="text-xs text-muted-foreground">{t('history.evidenceBadge')}</p>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setHistoryError(null);
              setHistoryTarget(row.original);
              setHistoryOpen(true);
            }}
          >
            {t('actions.edit')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={removeHistory.isPending}
            onClick={() => {
              void removeHistory.mutateAsync({ id: row.original.id }).then(refresh);
            }}
          >
            {t('actions.remove')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Upload progress, retry and the note that nothing here moves a price. */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-card-foreground">{t('documents.uploadTitle')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{sizeHint}</p>
          </div>
          <FileInput
            id="upload-report"
            label={t('documents.addReport')}
            onSelect={(file) => uploadInto('EXTERNAL_REPORT', file)}
            isBusy={isUploading && upload?.key.startsWith('upload:EXTERNAL_REPORT')}
          />
        </div>

        {upload === null ? null : (
          <div className="space-y-2 rounded-md border border-border bg-background p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="truncate text-sm text-foreground">{upload.fileName}</p>
              <span
                dir="ltr"
                className="tabular-nums text-xs text-muted-foreground"
              >
                {Math.round(upload.progress * 100)}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(upload.progress * 100)}
              aria-label={t('documents.uploadProgress')}
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
            >
              <div
                className={upload.error === null ? 'h-full bg-primary' : 'h-full bg-destructive'}
                style={{ width: `${Math.round(upload.progress * 100)}%` }}
              />
            </div>
            {upload.error === null ? null : (
              <div className="flex flex-wrap items-center gap-2">
                <p
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {upload.error}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => upload.retry?.()}
                >
                  <RotateCcw className="size-3.5" />
                  {t('actions.retry')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setUpload(null)}
                >
                  {t('actions.cancel')}
                </Button>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground">{t('documents.noValuationEffect')}</p>
      </section>

      {/* Photos — the five required slots, plus any damage photographs. */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-card-foreground">{t('documents.photosTitle')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('documents.photosBody')}</p>
          </div>
          <Badge tone={(photoSetQuery.data?.missing.length ?? 0) === 0 ? 'success' : 'muted'}>
            {t('documents.photoSetCount', {
              present: photoSetQuery.data?.present.length ?? 0,
              total: (photoSetQuery.data?.present.length ?? 0) + (photoSetQuery.data?.missing.length ?? 0),
            })}
          </Badge>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {REQUIRED_PHOTO_CATEGORIES.map((category) => {
            const document = byCategory(category);

            return (
              <div
                key={category}
                className="space-y-2"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="truncate text-xs font-medium text-card-foreground">{t(`enums.docCategory.${category}`)}</p>
                  <Badge tone={present.has(category) ? 'success' : 'muted'}>
                    {present.has(category) ? t('documents.present') : t('documents.missing')}
                  </Badge>
                </div>

                {document === null ? (
                  <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/40 p-3 text-center">
                    <p className="text-xs text-muted-foreground">{t('documents.slotEmpty')}</p>
                    <FileInput
                      id={`photo-${category}`}
                      label={t('documents.addPhoto')}
                      size="sm"
                      accept={acceptFor(category)}
                      capture
                      isBusy={isUploading && upload?.key.startsWith(`upload:${category}`)}
                      onSelect={(file) => uploadInto(category, file, t(`enums.docCategory.${category}`))}
                    />
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="block w-full text-start"
                      onClick={() => setPreview(document)}
                    >
                      <DocumentImage
                        documentId={document.id}
                        alt={t(`enums.docCategory.${category}`)}
                      />
                    </button>
                    <FileInput
                      id={`photo-replace-${category}`}
                      label={t('documents.replace')}
                      size="sm"
                      accept={acceptFor(category)}
                      capture
                      isBusy={isUploading && upload?.key === `replace:${document.id}`}
                      onSelect={(file) => replaceWith(document, file)}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.damagePhotos')}</h3>
            <FileInput
              id="photo-damage"
              label={t('documents.addDamagePhoto')}
              size="sm"
              accept={acceptFor('PHOTO_DAMAGE')}
              capture
              isBusy={isUploading && upload?.key.startsWith('upload:PHOTO_DAMAGE')}
              onSelect={(file) => uploadInto('PHOTO_DAMAGE', file)}
            />
          </div>

          {damagePhotos.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('documents.noDamagePhotos')}</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {damagePhotos.map((document) => (
                <button
                  key={document.id}
                  type="button"
                  className="block w-full text-start"
                  onClick={() => setPreview(document)}
                >
                  <DocumentImage
                    documentId={document.id}
                    alt={document.title}
                  />
                  <p className="mt-1 truncate text-xs text-muted-foreground">{document.title}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Licence — the one document with an expiry that must stay legible (PP §8). */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-card-foreground">{t('documents.licenceTitle')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('documents.licenceBody')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {licence?.expiryDate ? <Badge mono>{t('documents.expiresOn', { date: formatDay(licence.expiryDate) })}</Badge> : null}
            <FileInput
              id="licence-upload"
              label={licence === null ? t('documents.addLicence') : t('documents.replace')}
              size="sm"
              isBusy={isUploading && (upload?.key.startsWith('upload:LICENSE') || upload?.key === `replace:${licence?.id ?? ''}`)}
              onSelect={(file) => (licence === null ? uploadInto('LICENSE', file, t('enums.docCategory.LICENSE')) : replaceWith(licence, file))}
            />
          </div>
        </div>

        {licence === null ? (
          <p className="text-sm text-muted-foreground">{t('documents.noLicence')}</p>
        ) : (
          <div className="max-w-2xl">
            <DocumentFrame
              documentId={licence.id}
              mime={licence.mime}
              fileName={licence.originalName}
              title={licence.title}
            />
          </div>
        )}
      </section>

      {/* Reports and invoices. */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-card-foreground">{t('documents.reportsTitle')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('documents.reportsBody')}</p>
          </div>
          <FileInput
            id="upload-invoice"
            label={t('documents.addInvoice')}
            size="sm"
            isBusy={isUploading && upload?.key.startsWith('upload:INVOICE')}
            onSelect={(file) => uploadInto('INVOICE', file)}
          />
        </div>

        <DataTable
          columns={reportColumns}
          data={reports}
          isLoading={documentsQuery.isLoading}
          emptyMessage={t('documents.reportsEmpty')}
        />
      </section>

      {/* Manual service history — evidence, never a cost (PP AC07). */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-card-foreground">{t('history.title')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('history.body')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge mono>{formatMoney(historyQuery.data?.evidenceTotal ?? 0, { exact: true })}</Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setHistoryError(null);
                setHistoryTarget(null);
                setHistoryOpen(true);
              }}
            >
              <Plus className="size-3.5" />
              {t('history.add')}
            </Button>
          </div>
        </div>

        <DataTable
          columns={historyColumns}
          data={historyEvents}
          isLoading={historyQuery.isLoading}
          emptyMessage={t('history.empty')}
        />

        <p className="text-xs text-muted-foreground">{t('history.evidenceNote')}</p>
      </section>

      <Dialog
        isOpen={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.title ?? ''}
        description={preview === null ? undefined : t(`enums.docCategory.${preview.category}`)}
        className="max-w-3xl"
        footer={
          <Button
            variant="outline"
            onClick={() => setPreview(null)}
          >
            {t('actions.close')}
          </Button>
        }
      >
        {preview === null ? null : (
          <DocumentFrame
            documentId={preview.id}
            mime={preview.mime}
            fileName={preview.originalName}
            title={preview.title}
          />
        )}
      </Dialog>

      <DocumentMetaSheet
        isOpen={metaTarget !== null}
        document={metaTarget}
        costItems={costItems}
        isSaving={updateMeta.isPending}
        error={metaError}
        onClose={() => {
          setMetaTarget(null);
          setMetaError(null);
        }}
        onSubmit={(submission) => void onMetaSubmit(submission)}
      />

      <HistoryEventSheet
        isOpen={isHistoryOpen}
        event={historyTarget}
        documents={documents}
        isSaving={addHistory.isPending || updateHistory.isPending}
        error={historyError}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryTarget(null);
          setHistoryError(null);
        }}
        onSubmit={(submission) => void onHistorySubmit(submission)}
      />
    </div>
  );
};
